// Composer message-history recall folds (A-11). Split out of
// `app/chatDecider.ts` (C1 hotspot decomposition; verbatim). Pure and total
// over a `ChatSession`.

import {
    authorMessageHistory,
    type ChatSession,
    type ComposerHistoryNav,
} from "./model.js";

// Recall an OLDER sent message into the draft (A-11's Arrow-Up fold). From
// `at_draft`: capture the live draft as `savedDraft` and show the newest sent
// message (index 0). From `at_history`: step to the next-older entry when one
// exists, else stay put (the decider already refuses the no-op at the oldest
// entry; this clamp keeps evolve total regardless). The saved draft is captured
// ONCE and carried unchanged, so it survives any number of Up/Down presses.
export const recallOlder = (session: ChatSession): ChatSession => {
    const history = authorMessageHistory(session.conversation);
    if (history.length === 0) return session;
    const nav: ComposerHistoryNav =
        session.historyNav.type === "at_draft"
            ? { type: "at_history", index: 0, savedDraft: session.draft }
            : {
                  ...session.historyNav,
                  index: Math.min(
                      session.historyNav.index + 1,
                      history.length - 1,
                  ),
              };
    return {
        ...session,
        historyNav: nav,
        draft: history[nav.index] ?? session.draft,
    };
};

// Recall a NEWER sent message (A-11's Arrow-Down fold). Meaningful only while
// `at_history` (the decider refuses it at `at_draft`). Steps toward the newest
// entry; stepping PAST index 0 restores the once-saved draft and returns to
// `at_draft`. Total: a stray call at `at_draft` is a no-op.
export const recallNewer = (session: ChatSession): ChatSession => {
    if (session.historyNav.type === "at_draft") return session;
    if (session.historyNav.index === 0)
        return {
            ...session,
            draft: session.historyNav.savedDraft,
            historyNav: { type: "at_draft" },
        };
    const history = authorMessageHistory(session.conversation);
    const index = session.historyNav.index - 1;
    return {
        ...session,
        historyNav: { ...session.historyNav, index },
        draft: history[index] ?? session.draft,
    };
};
